'use client'

import { NAV_LINKS } from '@/config/navigation'
import { Link } from '@/i18n/navigation'
import { cn } from '@/lib/utils'
import { useTranslations } from 'next-intl'
import { useEffect, useRef, useState } from 'react'

interface IDesktopMenuProps {
  onClick: () => void
  isOpen: boolean
  onClose: () => void
}

export function DesktopMenu({ onClick, isOpen, onClose }: IDesktopMenuProps) {
  const t = useTranslations('Navigation')
  const menuRef = useRef<HTMLDivElement>(null)
  const [isVisible, setIsVisible] = useState(false)
  const [shouldRender, setShouldRender] = useState(false)

  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => setShouldRender(true))
      setTimeout(() => setIsVisible(true), 10)
    } else {
      requestAnimationFrame(() => setIsVisible(false))
      const timer = setTimeout(() => setShouldRender(false), 200)
      return () => clearTimeout(timer)
    }
  }, [isOpen])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Element
      const isClickOnBurgerButton =
        target.closest('#desktop-burger-button') !== null

      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !isClickOnBurgerButton
      ) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose])

  if (!shouldRender) return null

  return (
    <div
      ref={menuRef}
      className={cn(
        'shadow-faq absolute top-26 right-12 w-full max-w-125 rounded-3xl bg-white p-6 transition-all duration-200 ease-out',
        {
          'translate-y-0 scale-100 opacity-100': isVisible,
          '-translate-y-2.5 scale-95 opacity-0': !isVisible
        }
      )}
    >
      <nav className="w-full">
        <ul className="flex flex-col gap-2">
          {NAV_LINKS.map((link) => (
            <li
              key={link.label}
              className="text-body-2 text-primary-600 border-b-gray-200 px-2 py-2.5 leading-6 not-last:border-b"
            >
              <Link onClick={onClick} href={link.href}>
                {t(link.label)}
              </Link>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  )
}
